import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody } from '@this/shared/ui/feedbacks/modal';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { calcDistance, walkMinutesText } from '@this/src/util/utils';
import { toDomesticTel } from '@this/src/util';
import { formatPrice } from '@this/src/util/price/price.util';
import {
  fetchProposalItemGoogleReviews,
  fetchProposalItemHotelDetail,
  type GoogleReviews
} from '../../api/proposalBundles';
import type { HotelDetailData } from '../../types/proposalBundles';
import GoogleReviewsSection from './GoogleReviewsSection';
import HotelLocationMap from './HotelLocationMap';

interface Props {
  open: boolean;
  onClose: () => void;
  tripId: number;
  itemId: number;
}

const AMENITIES_LIMIT = 10;

const HotelDetailModal = ({ open, onClose, tripId, itemId }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<HotelDetailData | null>(null);
  const [googleReviews, setGoogleReviews] = useState<GoogleReviews>({
    rating: null,
    userRatingsTotal: null,
    reviews: []
  });
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showMorePropertyAmenities, setShowMorePropertyAmenities] = useState(false);
  const [showMoreRoomAmenities, setShowMoreRoomAmenities] = useState(false);

  useEffect(() => {
    if (!open) {
      setData(null);
      setGoogleReviews({
        rating: null,
        userRatingsTotal: null,
        reviews: []
      });
      setError(null);
      setSelectedImageIndex(0);
      setShowMorePropertyAmenities(false);
      setShowMoreRoomAmenities(false);
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setGoogleReviews({
        rating: null,
        userRatingsTotal: null,
        reviews: []
      });
      try {
        const result = await fetchProposalItemHotelDetail(tripId.toString(), itemId, signal);
        if (signal.aborted) return;

        setData(result);
        fetchProposalItemGoogleReviews(tripId.toString(), itemId, signal)
          .then(reviews => {
            if (signal.aborted) return;

            setGoogleReviews(reviews);
          })
          .catch(fetchError => {
            if (fetchError instanceof Error && fetchError.name === 'AbortError') return;
          });
      } catch (fetchError) {
        if (fetchError instanceof Error && fetchError.name === 'AbortError') return;
        if (signal.aborted) return;

        setError('ホテル詳細の取得に失敗しました');
      } finally {
        if (signal.aborted) return;

        setLoading(false);
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [open, tripId, itemId]);

  const handleClose = () => {
    onClose();
  };

  const hotelLocation = useMemo(() => {
    if (!data) return null;
    return { lat: data.hotel.latitude, lng: data.hotel.longitude };
  }, [data]);

  const distanceToDestination = useMemo(() => {
    if (!data || !data.destLocation || !data.hotel.latitude || !data.hotel.longitude) {
      return data?.hotel.distance ?? 0;
    }
    return calcDistance(data.destLocation.lat, data.destLocation.lng, data.hotel.latitude, data.hotel.longitude);
  }, [data]);

  return (
    <Modal open={open} onClose={handleClose} size="large">
      <ModalHeader>ホテル詳細</ModalHeader>
      <ModalBody>
        {loading && <div style={styles.loading}>読み込み中...</div>}
        {error && <div style={styles.error}>{error}</div>}
        {data && (
          <div style={styles.content}>
            <h3 style={styles.hotelName}>{data.hotel.name}</h3>
            <div style={styles.topSection}>
              <div style={styles.leftColumn}>
                <ImageGallery
                  images={data.hotel.images}
                  selectedIndex={selectedImageIndex}
                  onSelectImage={setSelectedImageIndex}
                  styles={styles}
                />
                <div style={styles.telSection}>
                  <span style={styles.telLabel}>電話番号</span>
                  <span style={styles.telValue}>{data.hotel.tel ? toDomesticTel(data.hotel.tel) : '-'}</span>
                </div>
              </div>

              <div style={styles.rightColumn}>
                <div style={styles.mapArea}>
                  {hotelLocation && (
                    <HotelLocationMap
                      hotelLocation={hotelLocation}
                      destLocation={data.destLocation}
                      height={240}
                    />
                  )}

                  <div style={styles.infoArea}>
                    <table style={styles.infoTable}>
                      <tbody>
                        <tr>
                          <td style={styles.infoLabel}>目的地まで</td>
                          <td style={styles.infoSeparator}>：</td>
                          <td style={styles.infoValue}>徒歩{walkMinutesText(distanceToDestination)}</td>
                        </tr>
                        <tr>
                          <td style={styles.infoLabel}>
                            最寄駅まで{data.hotel.nearestStationName ? `(${data.hotel.nearestStationName})` : ''}
                          </td>
                          <td style={styles.infoSeparator}>：</td>
                          <td style={styles.infoValue}>
                            {data.hotel.stationDistance > 0
                              ? `徒歩${walkMinutesText(data.hotel.stationDistance)}`
                              : '-'}
                          </td>
                        </tr>
                        <tr>
                          <td style={styles.infoLabel}>チェックイン</td>
                          <td style={styles.infoSeparator}>：</td>
                          <td style={styles.infoValue}>{data.hotel.checkin || '-'}</td>
                        </tr>
                        <tr>
                          <td style={styles.infoLabel}>チェックアウト</td>
                          <td style={styles.infoSeparator}>：</td>
                          <td style={styles.infoValue}>{data.hotel.checkout || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div style={styles.priceBox}>
                      <div style={styles.priceTotal}>
                        <span>ホテル料金合計：</span>
                        <span>{formatPrice(data.plan.priceWithTax)}</span>
                      </div>
                      <div style={styles.priceDetail}>
                        {data.plan.priceText?.split('\n').map(line => (
                          <p key={line} style={styles.priceDetailLine}>
                            {line || <br />}
                          </p>
                        ))}
                      </div>
                      {data.hotel.refundable != null && (
                        <div style={styles.cancelStatus}>※キャンセル：{data.hotel.refundable ? '可' : '不可'}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.sectionTitle}>選択中のプラン</div>
            <div style={styles.planSection}>
              <div style={styles.planInfo}>
                <div style={styles.planRoom}>{data.plan.roomName}</div>
                {data.plan.planName && <div style={styles.planName}>{data.plan.planName}</div>}
                <div style={styles.planAmenities}>
                  {data.plan.smoke != null && (
                    <span style={styles.planAmenityItem}>{data.plan.smoke ? '喫煙' : '禁煙'}</span>
                  )}
                  {data.plan.bedTypes && data.plan.bedTypes.length > 0 && (
                    <span style={styles.planAmenityItem}>
                      {data.plan.bedTypes.map(b => b.description).join(' / ')}
                    </span>
                  )}
                  {(data.plan.checkinTime || data.hotel.checkin) && (
                    <span style={styles.planAmenityItem}>
                      チェックイン：{data.plan.checkinTime || data.hotel.checkin}
                    </span>
                  )}
                </div>
              </div>
              <div style={styles.planPrice}>{data.plan.priceText}</div>
            </div>

            <div style={styles.bottomSection}>
              {data.hotel.propertyAmenities && data.hotel.propertyAmenities.length > 0 && (
                <div style={styles.bottomColumn}>
                  <div style={styles.sectionTitle}>施設の設備</div>
                  <ul style={styles.amenitiesList}>
                    {(showMorePropertyAmenities
                      ? data.hotel.propertyAmenities
                      : data.hotel.propertyAmenities.slice(0, AMENITIES_LIMIT)
                    ).map(amenity => (
                      <li key={amenity} style={styles.amenityListItem}>
                        {amenity}
                      </li>
                    ))}
                  </ul>
                  {data.hotel.propertyAmenities.length > AMENITIES_LIMIT && (
                    <button
                      type="button"
                      style={styles.toggleLink}
                      onClick={() => setShowMorePropertyAmenities(!showMorePropertyAmenities)}
                    >
                      {showMorePropertyAmenities ? '閉じる' : 'もっと見る'}
                    </button>
                  )}
                </div>
              )}

              {data.hotel.roomAmenities && data.hotel.roomAmenities.length > 0 && (
                <div style={styles.bottomColumn}>
                  <div style={styles.sectionTitle}>部屋の設備</div>
                  <ul style={styles.amenitiesList}>
                    {(showMoreRoomAmenities
                      ? data.hotel.roomAmenities
                      : data.hotel.roomAmenities.slice(0, AMENITIES_LIMIT)
                    ).map(amenity => (
                      <li key={amenity} style={styles.amenityListItem}>
                        {amenity}
                      </li>
                    ))}
                  </ul>
                  {data.hotel.roomAmenities.length > AMENITIES_LIMIT && (
                    <button
                      type="button"
                      style={styles.toggleLink}
                      onClick={() => setShowMoreRoomAmenities(!showMoreRoomAmenities)}
                    >
                      {showMoreRoomAmenities ? '閉じる' : 'もっと見る'}
                    </button>
                  )}
                </div>
              )}

              {(data.hotel.cancelPolicy ||
                data.hotel.specialCheckinInstructions ||
                data.hotel.checkinInstructions ||
                data.hotel.note ||
                data.hotel.parkingInformation ||
                data.plan.planDetail) && (
                <div style={styles.bottomColumn}>
                  {data.hotel.cancelPolicy && (
                    <>
                      <div style={styles.sectionTitle}>キャンセルポリシー</div>
                      <div style={styles.cancelPolicy}>{data.hotel.cancelPolicy}</div>
                    </>
                  )}

                  {(data.hotel.specialCheckinInstructions ||
                    data.hotel.checkinInstructions ||
                    data.hotel.note ||
                    data.hotel.parkingInformation ||
                    data.plan.planDetail) && (
                    <>
                      <div style={styles.sectionTitle}>備考</div>
                      <div style={styles.noteSection}>
                        {data.hotel.specialCheckinInstructions && (
                          // eslint-disable-next-line react/no-danger
                          <div
                            style={styles.noteItem}
                            dangerouslySetInnerHTML={{ __html: data.hotel.specialCheckinInstructions }}
                          />
                        )}
                        {data.hotel.checkinInstructions && (
                          // eslint-disable-next-line react/no-danger
                          <div
                            style={styles.noteItem}
                            dangerouslySetInnerHTML={{ __html: data.hotel.checkinInstructions }}
                          />
                        )}
                        {data.plan.planDetail && <div style={styles.noteItem}>{data.plan.planDetail}</div>}
                        {data.hotel.parkingInformation && (
                          <div style={styles.noteItem}>駐車場: {data.hotel.parkingInformation}</div>
                        )}
                        {data.hotel.note && <div style={styles.noteItem}>{data.hotel.note}</div>}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <GoogleReviewsSection googleReviews={googleReviews} />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

interface ImageGalleryProps {
  images: string[];
  selectedIndex: number;
  onSelectImage: (index: number) => void;
  styles: ReturnType<typeof getStyles>;
}

const MAX_THUMBNAILS = 26;

const ImageGallery = ({ images, selectedIndex, onSelectImage, styles }: ImageGalleryProps) => {
  if (images.length === 0) return null;

  const mainImage = images[selectedIndex] || images[0];
  const thumbnails = images.slice(0, MAX_THUMBNAILS);

  return (
    <div style={styles.imageGallery}>
      <div style={styles.mainImageContainer}>
        <div style={{ ...styles.mainImage, backgroundImage: `url(${mainImage})` }} />
      </div>
      <div style={styles.thumbnailContainer}>
        {thumbnails.map((image, index) => (
          <div
            key={image || `thumb_${index}`}
            role="button"
            tabIndex={0}
            style={{
              ...styles.thumbnail,
              backgroundImage: `url(${image})`,
              ...(index !== selectedIndex ? styles.thumbnailUnselected : {})
            }}
            onMouseOver={() => onSelectImage(index)}
            onFocus={() => onSelectImage(index)}
          />
        ))}
      </div>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  loading: {
    textAlign: 'center' as const,
    padding: '40px',
    color: theme.grayTextColor
  },
  error: {
    textAlign: 'center' as const,
    padding: '40px',
    color: '#d32f2f'
  },
  content: {
    padding: '16px'
  },
  hotelName: {
    fontSize: '18px',
    fontWeight: 700 as const,
    marginBottom: '16px',
    color: theme.textColor
  },
  topSection: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '24px',
    marginBottom: '24px',
    width: '100%'
  },
  leftColumn: {
    flex: 1,
    minWidth: '280px'
  },
  rightColumn: {
    flex: 1,
    minWidth: '280px'
  },
  mapArea: {
    width: '100%'
  },
  imageGallery: {
    marginBottom: '12px'
  },
  mainImageContainer: {
    border: `3px solid ${theme.hotelImageBorderColor || '#e0e0e0'}`,
    borderRadius: '3px'
  },
  mainImage: {
    width: '100%',
    height: '288px',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center'
  },
  thumbnailContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    backgroundColor: theme.grayColorLight || '#f5f5f5'
  },
  thumbnail: {
    width: '36px',
    height: '36px',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    cursor: 'pointer'
  },
  thumbnailUnselected: {
    backgroundBlendMode: 'color',
    backgroundColor: 'rgba(255, 255, 255, 0.7)'
  },
  telSection: {
    marginTop: '12px',
    fontSize: '13px'
  },
  telLabel: {
    fontWeight: 600 as const,
    marginRight: '8px',
    color: theme.grayTextColor
  },
  telValue: {
    color: theme.textColor
  },
  infoArea: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    fontSize: '12px'
  },
  infoTable: {
    tableLayout: 'auto' as const,
    width: 'auto',
    margin: 0,
    borderCollapse: 'collapse' as const
  },
  priceBox: {
    backgroundColor: '#ffffff',
    padding: '7px 10px',
    marginLeft: 'auto',
    whiteSpace: 'pre-line' as const
  },
  priceTotal: {
    fontSize: '14px',
    fontWeight: 700 as const,
    color: theme.linkColor,
    marginBottom: '4px'
  },
  priceDetail: {
    fontSize: '12px',
    color: theme.textColor
  },
  priceDetailLine: {
    margin: 0
  },
  cancelStatus: {
    fontSize: '12px',
    color: '#d32f2f',
    marginTop: '4px'
  },
  infoLabel: {
    borderBottom: 0,
    padding: 0,
    verticalAlign: 'top' as const,
    fontSize: '12px',
    color: theme.textColor
  },
  infoSeparator: {
    borderBottom: 0,
    padding: '0 4px',
    verticalAlign: 'top' as const,
    fontSize: '12px',
    color: theme.textColor
  },
  infoValue: {
    borderBottom: 0,
    padding: 0,
    verticalAlign: 'top' as const,
    fontSize: '12px',
    color: theme.textColor
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: 600 as const,
    marginBottom: '4px',
    color: theme.grayTextColor
  },
  planSection: {
    backgroundColor: '#f5f5f5',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  planInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  },
  planRoom: {
    fontSize: '13px',
    fontWeight: 500 as const
  },
  planName: {
    fontSize: '12px',
    color: theme.grayTextColor
  },
  planAmenities: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginTop: '4px',
    fontSize: '12px',
    color: theme.textColor
  },
  planAmenityItem: {
    whiteSpace: 'nowrap' as const
  },
  planPrice: {
    fontSize: '15px',
    fontWeight: 700 as const,
    color: theme.linkColor
  },
  bottomSection: {
    display: 'flex',
    gap: '16px'
  },
  bottomColumn: {
    flex: 1,
    minWidth: 0
  },
  amenitiesList: {
    margin: '0 0 16px 0',
    paddingLeft: '20px',
    fontSize: '12px',
    color: theme.textColor,
    lineHeight: 1.8
  },
  amenityListItem: {
    marginBottom: '2px'
  },
  toggleLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: '10px',
    color: theme.linkColor,
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  cancelPolicy: {
    backgroundColor: '#f5f5f5',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '10px',
    lineHeight: 1.4,
    whiteSpace: 'pre-line' as const,
    marginBottom: '12px'
  },
  noteSection: {
    backgroundColor: '#f5f5f5',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '10px',
    lineHeight: 1.4
  },
  noteItem: {
    marginBottom: '4px',
    whiteSpace: 'pre-line' as const
  }
});

export default HotelDetailModal;
